/**
 * The organisation's price list (cost round 3): what each model costs this organisation, with
 * dates, in US dollars. AIOE seeds it once from a bundled list (a starting point to be checked,
 * not a quote) and administrators override rates or add their own (negotiated rates), so every
 * machine prices the same call the same way and the ledger is priced by one list the
 * organisation owns.
 *
 *   GET   /catalogue/v1/pricing            a device or a person: the effective list, `digest`, ETag "<digest>"; 304 on If-None-Match
 *   GET   /admin/v1/pricing                administrators: the same, with where it came from
 *   PUT   /admin/v1/pricing                administrators: the whole list { entries, seats }; audited `pricing.update`
 *   PATCH /admin/v1/pricing/:id            administrators: one entry's rates (an override); it becomes the organisation's; audited `pricing.entry`
 *
 * **What a workbench does with it.** Read `GET /catalogue/v1/pricing` once and whenever the
 * fleet registration answer's `pricingDigest` (fleet.ts) differs from the digest it holds; send
 * `If-None-Match` with the digest it has. Price a call locally with {@link priceFor} and
 * {@link costOfCall} (mirror this file byte for byte, as with platform.ts) and send that figure
 * as `costUsd` with `priced: "list"`. The platform prices every record again on ingest from
 * its tokens with the entry in force at the record's `at`: the record is then stored with
 * `priced: "platform"` and the machine's own figure as `reportedUsd` (platform.ts). A call
 * with no matching entry keeps the machine's figure. Seats (`seats`) are not per call: a
 * workbench ignores them.
 *
 * Rates are US dollars per million tokens (`input`, `output`, `cacheRead`, `cacheWrite`), per
 * image produced (`perImage`) and per premium request (`perRequest`, GitHub Copilot). Cache
 * tokens are counted INSIDE `promptTokens` on the wire (platform.ts): subtract them before pricing the
 * uncached prompt at the input rate (`costOfCall` takes the uncached prompt), and they are
 * priced at their own rates, else at `input`.
 *
 * Words for people: price list, rate, negotiated rate, seat, starting list (the seed).
 */
import { z } from "zod";
/** The date of the bundled starting list (packages/api/src/pricing/seed.ts). */
export const PRICE_SEED_DATE = "2026-10-14";
const rate = z.number().nonnegative().max(100_000);
/** `YYYY-MM-DD`: the day (UTC) from which an entry is in force. */
export const priceDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a date as YYYY-MM-DD");
/** One rate line. `model` is a model name, or a prefix ending in `*` ("claude-sonnet-4-5*"); `provider` "*" matches any provider. */
export const priceEntrySchema = z.object({
    id: z.string().min(1).max(100),
    provider: z.string().min(1).max(100),
    model: z.string().min(1).max(200),
    effectiveFrom: priceDateSchema,
    /** US dollars per million prompt tokens. */
    input: rate,
    /** US dollars per million completion tokens. */
    output: rate,
    /** Per million prompt-cache reads and writes; `input` when absent. */
    cacheRead: rate.optional(),
    cacheWrite: rate.optional(),
    /** Per image the call produced. */
    perImage: rate.optional(),
    /** Per premium request (GitHub Copilot). An entry with only this rate prices calls that carry premium requests. */
    perRequest: rate.optional(),
    currency: z.literal("USD").default("USD"),
    /** `seed`: from the bundled starting list, unchanged. `organisation`: set or changed by an administrator. */
    source: z.enum(["seed", "organisation"]).default("organisation"),
    /** Why the rate is what it is ("negotiated, contract 2026-07"). */
    note: z.string().max(300).optional(),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
});
/** What an administrator sends for an entry: an id keeps it (a new one is made when absent). */
export const priceEntryInputSchema = priceEntrySchema.omit({ updatedAt: true, updatedBy: true, currency: true }).extend({ id: z.string().min(1).max(100).optional(), currency: z.literal("USD").optional() });
/** `PATCH /admin/v1/pricing/:id`: the fields to change; the entry becomes the organisation's. */
export const priceEntryPatchSchema = priceEntrySchema.pick({ provider: true, model: true, effectiveFrom: true, input: true, output: true, cacheRead: true, cacheWrite: true, perImage: true, perRequest: true, note: true }).partial();
/**
 * Seats on a subscription (Claude Max or Team, ChatGPT, GitHub Copilot): a fixed monthly cost per
 * vendor, shown on the chargeback statement and shared across business areas and projects by their
 * share of that vendor's subscription-billed calls in the month. `vendor` is a provider name
 * ({@link vendorOf}: anthropic, openai, google, github-copilot).
 */
export const seatSchema = z.object({
    vendor: z.string().min(1).max(100),
    /** What one seat costs a month, in US dollars. */
    monthlyUsd: z.number().nonnegative().max(1_000_000),
    count: z.number().int().nonnegative().max(1_000_000),
    /** The plan, for people ("Copilot Business"). */
    note: z.string().max(200).optional(),
});
/** `PUT /admin/v1/pricing`: the whole list. */
export const priceListPutSchema = z.object({
    entries: z.array(priceEntryInputSchema).max(2000),
    seats: z.array(seatSchema).max(100).default([]),
});
// ── Matching and pricing (mirror byte for byte) ────────────────────────────
/** Provider names as machines report them, to the provider a price list names. */
const PROVIDER_ALIASES = {
    claude: "anthropic",
    "anthropic-api": "anthropic",
    "azure-openai": "openai",
    azure: "openai",
    "openai-codex": "openai",
    codex: "openai",
    gemini: "google",
    "google-ai": "google",
    vertex: "google",
    "vertex-ai": "google",
    copilot: "github-copilot",
    github: "github-copilot",
    "github-models": "github-copilot",
};
/** A provider (or a seat's vendor) as the price list names it: lower case, aliases folded. */
export function vendorOf(provider) {
    const p = provider.trim().toLowerCase();
    return PROVIDER_ALIASES[p] ?? p;
}
/** A model name made comparable: lower case, a provider prefix dropped, dots as dashes ("claude-sonnet-4.5" = "claude-sonnet-4-5"). */
export function normaliseModel(model) {
    let m = model.trim().toLowerCase();
    const slash = m.lastIndexOf("/");
    if (slash >= 0)
        m = m.slice(slash + 1);
    m = m.replace(/\./g, "-");
    const claude = m.indexOf("claude-");
    if (claude > 0)
        m = m.slice(claude);
    return m;
}
/** Whether an entry prices only premium requests (no token rates). */
const requestsOnly = (e) => e.input === 0 && e.output === 0 && (e.perRequest ?? 0) > 0;
/**
 * The entry in force for a call: the provider's (or "*"), the model's exact name over the longest
 * prefix, the latest `effectiveFrom` on or before the call's day, and an organisation entry over
 * the seed's. An entry with only a premium-request rate matches only calls that carry premium
 * requests. Undefined when nothing matches.
 */
export function priceFor(entries, call) {
    const provider = vendorOf(call.provider);
    const model = normaliseModel(call.model);
    const day = call.at.slice(0, 10);
    let best;
    for (const e of entries) {
        const ep = vendorOf(e.provider);
        if (ep !== "*" && ep !== provider)
            continue;
        if (e.effectiveFrom > day)
            continue;
        if (requestsOnly(e) && !(call.premiumRequests && call.premiumRequests > 0))
            continue;
        const pattern = e.model.endsWith("*") ? normaliseModel(e.model.slice(0, -1)) : normaliseModel(e.model);
        const exact = !e.model.endsWith("*");
        if (exact ? model !== pattern : !model.startsWith(pattern))
            continue;
        const rank = [exact ? 1 : 0, pattern.length, ep === "*" ? 0 : 1, Date.parse(e.effectiveFrom), e.source === "organisation" ? 1 : 0];
        if (!best || compareRank(rank, best.rank) > 0)
            best = { e, rank };
    }
    return best?.e;
}
function compareRank(a, b) {
    for (let i = 0; i < a.length; i++)
        if (a[i] !== b[i])
            return a[i] - b[i];
    return 0;
}
/** What a call costs at an entry's rates, in US dollars (to the millionth). */
/**
 * The cost of one call at an entry's rates. `promptTokens` here is the UNCACHED prompt: on the wire
 * (platform.ts) cache tokens are counted inside `promptTokens`, so subtract `cacheReadTokens` and
 * `cacheWriteTokens` from it before calling, as the platform does on arrival.
 */
export function costOfCall(e, u) {
    const perMillion = u.promptTokens * e.input + u.completionTokens * e.output + (u.cacheReadTokens ?? 0) * (e.cacheRead ?? e.input) + (u.cacheWriteTokens ?? 0) * (e.cacheWrite ?? e.input);
    const usd = perMillion / 1_000_000 + (u.images ?? 0) * (e.perImage ?? 0) + (u.premiumRequests ?? 0) * (e.perRequest ?? 0);
    return Math.round(usd * 1_000_000) / 1_000_000;
}
/** The fixed monthly cost of the seats, per vendor. */
export function seatsMonthlyUsd(seats) {
    return Math.round(seats.reduce((t, s) => t + s.monthlyUsd * s.count, 0) * 100) / 100;
}
//# sourceMappingURL=pricing.js.map